import React, { useState, useEffect } from 'react';
import {
  Grid, Card, CardContent, CardMedia, Typography, Box, Button, Avatar, Chip, Stack,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider,
  CircularProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SchoolIcon from '@mui/icons-material/School';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatPhone, formatDate } from '../../utils/formatters';
import { getKYCList, approveKYC, deleteAstrologer } from '../../api/endpoints/astrologers';
import { API_URL } from '../../utils/url';

const KYCVerification = () => {
  const [kycList, setKycList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [imageView, setImageView] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [actionProcessing, setActionProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [kycFilter, setKycFilter] = useState('pending');

  useEffect(() => {
    fetchKYC();
  }, [kycFilter]);

  const fetchKYC = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (kycFilter !== 'all') params.kycStatus = kycFilter;
      const res = await getKYCList(params);
      setKycList(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load KYC documents');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (id, status) => {
    setActionId(id);
    setActionStatus(status);
    setActionReason('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (actionStatus === 'rejected' && (!actionReason || actionReason.trim().length < 3)) {
      toast.error('Please provide a valid reason for rejection');
      return;
    }
    try {
      setActionProcessing(true);
      await approveKYC(actionId, actionStatus, actionStatus === 'rejected' ? actionReason : '');
      toast.success(`KYC ${actionStatus} successfully`);
      setActionDialogOpen(false);
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${actionStatus} KYC`);
    } finally {
      setActionProcessing(false);
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      setDeleteProcessing(true);
      await deleteAstrologer(deleteId);
      toast.success('Astrologer refused and all data permanently deleted');
      setDeleteDialogOpen(false);
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="KYC Verification" subtitle="Review astrologer documents and approve or reject" />

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {[
          { value: 'pending', label: 'Pending' },
          { value: 'verified', label: 'Verified' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'all', label: 'All' },
        ].map((f) => (
          <Button
            key={f.value}
            variant={kycFilter === f.value ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setKycFilter(f.value)}
            sx={{ borderRadius: '20px' }}
          >
            {f.label}
          </Button>
        ))}
      </Stack>

      {kycList.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No {kycFilter !== 'all' ? kycFilter : ''} KYC applications found.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {kycList.map((kyc) => {
            const name = kyc.userId?.name || kyc.name || 'Unknown';
            const phone = kyc.userId?.phone || kyc.phone;
            const email = kyc.userId?.email || kyc.email;
            const avatar = kyc.userId?.avatar || kyc.avatar;
            const isExpanded = expanded === kyc._id;
            const isPending = kyc.kycStatus === 'pending';

            return (
              <Grid key={kyc._id} item xs={12}>
                <Card
                  sx={{
                    border: isPending ? '2px solid' : '1px solid',
                    borderColor: isPending ? 'warning.light' : 'divider',
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {avatar ? (
                          <Avatar src={API_URL?.API_BASE_URL+ avatar} sx={{ width: 56, height: 56 }} />
                        ) : (
                          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 24 }}>
                            {(name || '').split(' ').map(n => n[0]).join('')}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="h6">{name}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <StatusChip status={kyc.kycStatus} />
                            <Typography variant="caption" color="text.secondary">
                              Registered: {kyc.createdAt ? formatDate(kyc.createdAt) : '-'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {isPending && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => openActionDialog(kyc._id, 'verified')}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => openActionDialog(kyc._id, 'rejected')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          variant={isExpanded ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setExpanded(isExpanded ? null : kyc._id)}
                          sx={{ minWidth: 80 }}
                        >
                          {isExpanded ? 'Less' : 'Details'}
                        </Button>
                        <Tooltip title="Permanently delete this application and all uploaded data">
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(kyc._id)}>
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {kyc.kycStatus === 'rejected' && kyc.kycRejectedReason && (
                      <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1, p: 1.5, mb: 2 }}>
                        <Typography variant="caption" color="error" fontWeight={600}>Rejection Reason:</Typography>
                        <Typography variant="body2" color="error">{kyc.kycRejectedReason}</Typography>
                      </Box>
                    )}

                    {isExpanded && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                              Contact Information
                            </Typography>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">{formatPhone(phone)}</Typography>
                                {phone && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    href={`tel:${phone}`}
                                    startIcon={<PhoneIcon />}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                  >
                                    Call
                                  </Button>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">{email || '-'}</Typography>
                              </Box>
                            </Stack>

                            <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1.5, color: 'text.secondary' }}>
                              Professional Details
                            </Typography>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SchoolIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>Specialization:</strong>{' '}
                                  {Array.isArray(kyc.specializations) ? kyc.specializations.join(', ') : kyc.specializations || '-'}
                                </Typography>
                              </Box>
                              <Typography variant="body2">
                                <strong>Experience:</strong> {kyc.experience || 0} years
                              </Typography>
                              <Typography variant="body2">
                                <strong>Languages:</strong>{' '}
                                {Array.isArray(kyc.languages) ? kyc.languages.join(', ') : kyc.languages || '-'}
                              </Typography>
                              {kyc.bio && (
                                <Typography variant="body2">
                                  <strong>Bio:</strong> {kyc.bio}
                                </Typography>
                              )}
                            </Stack>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                              Rates
                            </Typography>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MonetizationOnIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  Chat: ₹{kyc.chatRate || 0}/min | Call: ₹{kyc.callRate || 0}/min | Video: ₹{kyc.videoCallRate || 0}/min
                                </Typography>
                              </Box>
                            </Stack>

                            <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1.5, color: 'text.secondary' }}>
                              KYC Documents
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                              {kyc.kycDocuments?.aadhaarFront && (
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>Aadhaar Front</Typography>
                                  <Box
                                    component="img"
                                    src={API_URL?.API_BASE_URL+ kyc.kycDocuments.aadhaarFront}
                                    sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                                    onClick={() => setImageView(kyc.kycDocuments.aadhaarFront)}
                                  />
                                </Box>
                              )}
                              {kyc.kycDocuments?.aadhaarBack && (
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>Aadhaar Back</Typography>
                                  <Box
                                    component="img"
                                    src={API_URL?.API_BASE_URL+kyc.kycDocuments.aadhaarBack}
                                    sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                                    onClick={() => setImageView(kyc.kycDocuments.aadhaarBack)}
                                  />
                                </Box>
                              )}
                              {kyc.kycDocuments?.panCard && (
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>PAN Card</Typography>
                                  <Box
                                    component="img"
                                    src={API_URL?.API_BASE_URL+ kyc.kycDocuments.panCard}
                                    sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                                    onClick={() => setImageView(kyc.kycDocuments.panCard)}
                                  />
                                </Box>
                              )}
                              {!kyc.kycDocuments?.aadhaarFront && !kyc.kycDocuments?.panCard && (
                                <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={!!imageView} onClose={() => setImageView(null)} maxWidth="md">
        <DialogContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            onClick={() => setImageView(null)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>
          {imageView && (
            <Box
              component="img"
              src={API_URL?.API_BASE_URL+imageView}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 1 }}
              alt="Document"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialogOpen} onClose={() => !actionProcessing && setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionStatus === 'verified' ? 'Approve KYC' : 'Reject & Delete KYC'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionStatus === 'verified'
              ? 'Are you sure you want to approve this KYC? The astrologer will be able to start accepting clients.'
              : 'Rejecting will permanently delete the astrologer\'s account, all uploaded documents, and associated data. This action cannot be undone.'}
          </Typography>
          {actionStatus === 'rejected' && (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Reason for Rejection"
              placeholder="At least 3 characters"
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              error={actionReason.length > 0 && actionReason.trim().length < 3}
              helperText={actionReason.length > 0 && actionReason.trim().length < 3 ? 'At least 3 characters' : ''}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={actionProcessing}>Cancel</Button>
          <Button
            variant="contained"
            color={actionStatus === 'verified' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={actionProcessing}
            startIcon={actionProcessing ? <CircularProgress size={16} /> : null}
          >
            {actionProcessing ? 'Processing...' : (actionStatus === 'verified' ? 'Approve' : 'Reject & Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Astrologer?"
        message="This will permanently delete the astrologer's account, all uploaded documents, and associated data. This action cannot be undone."
        confirmText="Delete Forever"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        severity="error"
        loading={deleteProcessing}
      />
    </Box>
  );
};

export default KYCVerification;
